import { api } from './api';

export type SystemHealthMetric = {
  value: number;
  unit?: string;
  change: number;
};

export type SystemHealthService = {
  name: string;
  status: string;
  detail: string;
};

export type PostgreSqlHealth = {
  status: string;
  detail?: string;
  latency_ms?: number;
};

export type SystemHealthResponse = {
  status: string;
  metrics: {
    api_uptime: SystemHealthMetric;
    average_response_time: SystemHealthMetric;
    active_sessions: SystemHealthMetric;
    error_rate: SystemHealthMetric;
  };
  services: SystemHealthService[];
  postgres_health?: PostgreSqlHealth;
};

export type SystemLog = {
  id: string;
  actor: string;
  action: string;
  target: string;
  time: string;
};

export type SystemLogsResponse = {
  logs: SystemLog[];
  total: number;
};

export type AdminInvitation = {
  id: string;
  userId?: string;
  name: string;
  email: string;
  status: string;
  isActive?: boolean;
  createdAt: string;
};

export type AdminInvitationsResponse = {
  invitations: AdminInvitation[];
  total: number;
};

export type AdminUser = {
  id: string;
  name: string;
  email: string;
  username: string;
  isActive: boolean;
  createdAt: string;
};

export type AdminUsersResponse = {
  admins: AdminUser[];
  total: number;
  limit: number;
  offset: number;
};

export async function getSystemHealth() {
  const payload = await api.get<SystemHealthResponse | { data: SystemHealthResponse }>('/super-admin/system-health');
  return 'data' in payload ? payload.data : payload;
}

export async function getSystemLogs(limit = 50, offset = 0, search = ''): Promise<SystemLogsResponse> {
  const query = `limit=${limit}&offset=${offset}${search.trim() ? `&search=${encodeURIComponent(search.trim())}` : ''}`;
  const payload = await api.get<unknown>(`/super-admin/audit-logs?${query}`);
  return normalizeSystemLogs(payload);
}

export function createAdminInvitation(payload: { full_name: string; email: string }) {
  return api.post('/super-admin/admin-invitations', payload);
}

export async function getAdminInvitations(limit = 50, offset = 0, search = ''): Promise<AdminInvitationsResponse> {
  const query = `limit=${limit}&offset=${offset}${search.trim() ? `&search=${encodeURIComponent(search.trim())}` : ''}`;
  const payload = await api.get<unknown>(`/super-admin/admin-invitations?${query}`);
  return normalizeAdminInvitations(payload);
}

export function activateAdminInvitation(invitationId: string) {
  return api.post(`/super-admin/admin-invitations/${invitationId}/activate`);
}

export function rejectAdminInvitation(invitationId: string, reason: string) {
  return api.post(`/super-admin/admin-invitations/${invitationId}/reject`, { reason });
}

export function resendAdminInvitation(invitationId: string) {
  return api.post(`/super-admin/admin-invitations/${invitationId}/resend`);
}

export function deactivateAdminUser(userId: string, reason: string) {
  return api.post(`/super-admin/admins/${userId}/deactivate`, { reason });
}

export function reactivateAdminUser(userId: string) {
  return api.post(`/super-admin/admins/${userId}/activate`);
}

export async function getAdminUsers(filters: {
  limit?: number;
  offset?: number;
  search?: string;
  status?: 'active' | 'inactive' | '';
} = {}): Promise<AdminUsersResponse> {
  const params = new URLSearchParams({
    limit: String(filters.limit ?? 50),
    offset: String(filters.offset ?? 0),
  });
  if (filters.search?.trim()) params.set('search', filters.search.trim());
  if (filters.status) params.set('status', filters.status);
  const payload = await api.get<unknown>(`/super-admin/admins?${params.toString()}`);
  return normalizeAdminUsers(payload);
}

export function acceptAdminInvitation(token: string, username: string, password: string) {
  return api.post('/auth/admin-invitations/complete', { token, username, password });
}

export function normalizeAdminInvitations(payload: unknown): AdminInvitationsResponse {
  const source = isObject(payload) ? payload : undefined;
  const nestedData = source && isObject(source.data) ? source.data : undefined;
  const entries = Array.isArray(payload)
    ? payload
    : readArray(source, 'invitations', 'items', 'results')
      ?? readArray(nestedData, 'invitations', 'items', 'results')
      ?? (source && Array.isArray(source.data) ? source.data : []);
  const invitations = entries.map(normalizeAdminInvitation).filter(isAdminInvitation);
  const total = readNumber(source, 'total', 'count')
    ?? readNumber(nestedData, 'total', 'count')
    ?? invitations.length;

  return { invitations, total };
}

export function normalizeAdminUsers(payload: unknown): AdminUsersResponse {
  const source = isObject(payload) ? payload : undefined;
  const nestedData = source && isObject(source.data) ? source.data : undefined;
  const entries = Array.isArray(payload)
    ? payload
    : readArray(source, 'admins', 'users', 'items', 'results')
      ?? readArray(nestedData, 'admins', 'users', 'items', 'results')
      ?? (source && Array.isArray(source.data) ? source.data : []);
  const admins = entries.map(normalizeAdminUser).filter(isAdminUser);
  const total = readNumber(source, 'total', 'count')
    ?? readNumber(nestedData, 'total', 'count')
    ?? admins.length;
  const limit = readNumber(source, 'limit')
    ?? readNumber(nestedData, 'limit')
    ?? 50;
  const offset = readNumber(source, 'offset')
    ?? readNumber(nestedData, 'offset')
    ?? 0;

  return { admins, total, limit, offset };
}

export function normalizeSystemLogs(payload: unknown): SystemLogsResponse {
  const source = isObject(payload) ? payload : undefined;
  const nestedData = source && isObject(source.data) ? source.data : undefined;
  const entries = Array.isArray(payload)
    ? payload
    : readArray(source, 'logs', 'items', 'results')
      ?? readArray(nestedData, 'logs', 'items', 'results')
      ?? (source && Array.isArray(source.data) ? source.data : []);
  const logs = entries.map(normalizeSystemLog).filter(isSystemLog);
  const total = readNumber(source, 'total', 'count')
    ?? readNumber(nestedData, 'total', 'count')
    ?? logs.length;

  return { logs, total };
}

function normalizeSystemLog(payload: unknown): SystemLog | null {
  if (!isObject(payload)) return null;

  const actor = readString(payload, 'actor', 'actor_name', 'user', 'username');
  const action = readString(payload, 'action', 'event', 'operation');
  const target = readString(payload, 'target', 'resource', 'entity');
  const time = readString(payload, 'time', 'created_at', 'timestamp');

  if (!actor || !action || !target || !time) return null;

  return {
    id: readString(payload, 'id', 'log_id') ?? `${actor}-${action}-${target}-${time}`,
    actor,
    action,
    target,
    time,
  };
}

function normalizeAdminInvitation(payload: unknown): AdminInvitation | null {
  if (!isObject(payload)) return null;

  const user = readObject(payload, 'user', 'admin', 'admin_user');
  const id = readString(payload, 'id', 'invitation_id');
  const email = readString(payload, 'email', 'invitee_email')
    ?? readString(user, 'email');
  if (!id || !email) return null;

  return {
    id,
    userId: readString(payload, 'user_id', 'admin_user_id')
      ?? readString(user, 'id', 'user_id'),
    email,
    name: readString(payload, 'name', 'full_name', 'invitee_name')
      ?? readString(user, 'name', 'full_name', 'username')
      ?? email,
    status: readString(payload, 'status', 'invitation_status', 'state') ?? 'pending',
    isActive: readBoolean(payload, 'is_active', 'active')
      ?? readBoolean(user, 'is_active', 'active'),
    createdAt: readString(payload, 'created_at', 'invited_at', 'time') ?? '',
  };
}

function normalizeAdminUser(payload: unknown): AdminUser | null {
  if (!isObject(payload)) return null;
  const id = readString(payload, 'user_id', 'id');
  const email = readString(payload, 'email');
  if (!id || !email) return null;

  const username = readString(payload, 'username') ?? email;
  return {
    id,
    email,
    username,
    name: readString(payload, 'name', 'full_name') ?? username,
    isActive: readBoolean(payload, 'is_active', 'active') ?? false,
    createdAt: readString(payload, 'created_at', 'joined_at') ?? '',
  };
}

function isAdminInvitation(invitation: AdminInvitation | null): invitation is AdminInvitation {
  return invitation !== null;
}

function isAdminUser(admin: AdminUser | null): admin is AdminUser {
  return admin !== null;
}

function isSystemLog(log: SystemLog | null): log is SystemLog {
  return log !== null;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function readArray(source: Record<string, unknown> | undefined, ...keys: string[]) {
  for (const key of keys) {
    const value = source?.[key];
    if (Array.isArray(value)) return value;
  }
  return undefined;
}

function readString(source: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value) return value;
  }
  return undefined;
}

function readNumber(source: Record<string, unknown> | undefined, ...keys: string[]) {
  for (const key of keys) {
    const value = source?.[key];
    if (typeof value === 'number') return value;
  }
  return undefined;
}

function readBoolean(source: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'boolean') return value;
  }
  return undefined;
}

function readObject(source: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = source[key];
    if (isObject(value)) return value;
  }
  return {};
}
